import React from 'react';
import { Image as ImageIcon } from './icons';
import { Modal, Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { PROFILE_BACKGROUNDS } from '../theme/profileBackgrounds';
import { colors, radius, screenPadding, spacing, typography } from '../theme/tokens';
import { t } from '../i18n';

export interface BackgroundPickerProps {
  visible: boolean;
  title?: string;
  selectedKey?: string | null;
  onSelect: (key: string) => void;
  onImport?: () => void;
  onDismiss?: () => void; // appelé une fois le sheet entièrement fermé (iOS) — cf. course picker natif
  onClose: () => void;
  testID?: string;
}

// Choix d'un fond : import d'une photo (galerie) OU fond coloré prédéfini (bannière ou avatar).
export function BackgroundPicker({ visible, title = 'Choisir un fond', selectedKey, onSelect, onImport, onDismiss, onClose, testID }: BackgroundPickerProps) {
  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose} onDismiss={onDismiss}>
      <Pressable style={styles.backdrop} onPress={onClose} accessibilityRole="button" />
      <View testID={testID} style={styles.sheet}>
        <View style={styles.handleRow}>
          <Text style={styles.title}>{title}</Text>
          <Pressable testID="bgpick-close" accessibilityRole="button" accessibilityLabel={t('a11y.close')} onPress={onClose} hitSlop={8}>
            <Text style={styles.close}>✕</Text>
          </Pressable>
        </View>

        {onImport ? (
          <Pressable
            testID="bgpick-import"
            accessibilityRole="button"
            accessibilityLabel={t('a11y.importPhoto')}
            onPress={onImport}
            style={styles.importBtn}
          >
            <ImageIcon size={18} color={colors.bluePrimary} strokeWidth={2} />
            <Text style={styles.importText}>{t('misc.importPhoto')}</Text>
          </Pressable>
        ) : null}

        <Text style={styles.orLabel}>{t('misc.orColour')}</Text>

        <View style={styles.grid}>
          {PROFILE_BACKGROUNDS.map((bg) => {
            const selected = bg.key === selectedKey;
            return (
              <Pressable
                key={bg.key}
                testID={`bgpick-${bg.key}`}
                accessibilityRole="button"
                accessibilityLabel={bg.label}
                onPress={() => onSelect(bg.key)}
                style={[styles.swatch, { backgroundColor: bg.color }, selected && styles.swatchSelected]}
              >
                {selected ? <Text style={styles.check}>✓</Text> : null}
              </Pressable>
            );
          })}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.35)' },
  sheet: {
    backgroundColor: colors.white,
    borderTopLeftRadius: radius.xl,
    borderTopRightRadius: radius.xl,
    padding: screenPadding,
    paddingBottom: spacing.xxl,
    gap: spacing.lg,
  },
  handleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  title: { fontSize: typography.h2.size, fontWeight: typography.h2.weight, color: colors.grey900 },
  close: { fontSize: typography.h3.size, color: colors.grey500 },
  importBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.bluePrimary,
    backgroundColor: colors.blueSoft,
  },
  importText: { fontSize: typography.bodyMd.size, fontWeight: typography.bodyMd.weight, color: colors.bluePrimary },
  orLabel: {
    fontSize: typography.caption.size,
    fontWeight: typography.caption.weight,
    letterSpacing: typography.caption.letterSpacing,
    color: colors.grey500,
  },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md },
  swatch: {
    width: 56,
    height: 56,
    borderRadius: radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  swatchSelected: { borderColor: colors.grey900 },
  check: { color: colors.white, fontSize: typography.h3.size, fontWeight: '700' },
});
